/**
 * The API's cell equality, so a local count can follow the server's rules.
 *
 * Mirrors `make_tolerance_comparator()` in `API/src/services/merge/classification.py`
 * with its default of 6 decimal places (`MERGE_EQUALITY_DECIMAL_PLACES`):
 * `null` equals only `null`; two finite decimals are equal when they are equal
 * after rounding to 6 places, half to even; anything else (a value that is not
 * a finite decimal, or one too large to round) is equal only when the strings
 * are identical.
 */
const PLACES = 6;
// The API's decimal context precision: a rounded value with more digits than
// this cannot be quantized there, and falls through to string equality.
const MAX_DIGITS = 28;

// Python's Decimal(str): optional sign, digits with optional single
// underscores between them, optional fraction and exponent, surrounding
// whitespace ignored. Infinity and NaN are not finite, so they are not here.
const DECIMAL =
  /^[+-]?(?:\d(?:_?\d)*(?:\.(?:\d(?:_?\d)*)?)?|\.\d(?:_?\d)*)(?:[eE][+-]?\d+)?$/;

/** The value rounded to `PLACES` as a scaled integer, or null if it is not a finite decimal that fits. */
function quantize(raw: string): bigint | null {
  const text = raw.trim();
  if (!DECIMAL.test(text)) {
    return null;
  }
  const negative = text.startsWith('-');
  const unsigned = text.replace(/^[+-]/, '').replace(/_/g, '');
  const [mantissa, exponentText] = unsigned.toLowerCase().split('e');
  const [whole, fraction = ''] = mantissa.split('.');
  const digits = `${whole}${fraction}`.replace(/^0+(?=\d)/, '') || '0';
  // value = digits × 10^scale
  const scale = Number(exponentText ?? '0') - fraction.length;
  const shift = scale + PLACES;

  let result: bigint;
  if (shift >= 0) {
    if (digits !== '0' && digits.length + shift > MAX_DIGITS) {
      return null;
    }
    result = BigInt(digits) * 10n ** BigInt(shift);
  } else {
    const drop = -shift;
    if (digits.length - drop > MAX_DIGITS) {
      return null;
    }
    if (drop > digits.length + 1) {
      result = 0n;
    } else {
      const divisor = 10n ** BigInt(drop);
      const n = BigInt(digits);
      const quotient = n / divisor;
      const remainder = n % divisor;
      const twice = remainder * 2n;
      const roundUp =
        twice > divisor || (twice === divisor && quotient % 2n === 1n);
      result = roundUp ? quotient + 1n : quotient;
    }
  }
  return negative ? -result : result;
}

export function valuesEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (a === null || b === null || a === undefined || b === undefined) {
    return (a ?? null) === null && (b ?? null) === null;
  }
  const qa = quantize(a);
  const qb = quantize(b);
  if (qa !== null && qb !== null) {
    return qa === qb;
  }
  return a === b;
}
